import { useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { OnboardingPayloadData, useOnboardingContext } from '@/contexts/onboardingContext';
import useTranslate from '@/hooks/useTranslate';
import { useInsuranceTypes } from '@/pages/members/hooks/useInsuranceTypes';
import { STEPS_PATH_TRANSLATION_KEY } from '@/pages/members/pages/Onboarding/constants';
import StepComponent from '@/pages/members/pages/Onboarding/steps/StepComponent';
import { StepProps } from '@/pages/members/pages/Onboarding/steps/types';
import { insuranceTypeLabel } from '@/pages/members/pages/Onboarding/utils/insuranceTypeLabel';
import Alert from '@/ui/members/components/Alert';
import FieldError from '@/ui/members/components/form/FieldError';
import InputGroup from '@/ui/members/components/form/InputGroup';
import RadioGroup from '@/ui/members/components/form/RadioGroup';
import Spinner from '@/ui/members/components/Spinner';
import Heading from '@/ui/members/components/typography/Heading';

import getSubmittedData from '../../utils/getSubmittedData';

interface InsuranceTypeFormValues extends OnboardingPayloadData {
  customInsuranceProvider?: string;
  insuranceProvider: string;
  insuranceType: string;
}

interface Props extends StepProps {
  /**
   * Whether to show all content or a minimal version
   */
  showAllContent?: boolean;
}

const InsuranceTypeSelectionStep = ({ nodeType, showAllContent = true, uid }: Props) => {
  const {
    data: { jurisdiction },
    steps
  } = useOnboardingContext();
  const { t } = useTranslate(`${STEPS_PATH_TRANSLATION_KEY}.InsuranceTypeStep`);

  const insuranceTypeSublabelMap: { [key: string]: string } = useMemo(
    () => ({
      Employer: t('sublabelEmployer'),
      Medicaid: t('sublabelMedicaid'),
      Medicare: t('sublabelMedicare'),
      Marketplace: t('sublabelMarketplace')
    }),
    [t]
  );

  const {
    data: insuranceTypesResponse,
    isPending: isPendingInsuranceTypes,
    isError: isErrorInsuranceTypes
  } = useInsuranceTypes({ onboarding: true });

  const formattedInsuranceTypes = useMemo(() => {
    return (
      insuranceTypesResponse?.map(({ label, value }) => ({
        label: insuranceTypeLabel({ jurisdiction: jurisdiction as string, label }),
        subLabel: insuranceTypeSublabelMap[value] || '',
        value
      })) || []
    );
  }, [insuranceTypeSublabelMap, insuranceTypesResponse, jurisdiction]);

  const {
    control,
    handleSubmit,
    formState: { errors, isValid }
  } = useForm<InsuranceTypeFormValues>({
    mode: 'onSubmit',
    defaultValues: (getSubmittedData(nodeType, uid, steps) as InsuranceTypeFormValues) || {
      insuranceType: undefined
    }
  });

  return (
    <StepComponent handleSubmit={handleSubmit} uid={uid} nodeType={nodeType} isValid={isValid}>
      {isErrorInsuranceTypes && (
        <Alert level="error">
          <p>{t('errorFetchingTypes')}</p>
        </Alert>
      )}

      <div className="text-center mb-4">
        <Heading value={1} className="mb-2">
          {t('heading')}
        </Heading>
        {showAllContent && <p className="text-base mb-3">{t('introMessage')}</p>}
      </div>

      <div className="py-0.5">
        <InputGroup>
          <div className="mb-6">
            <Controller
              name="insuranceType"
              control={control}
              rules={{ required: t('insuranceTypeRequired') }}
              render={({ field }) => (
                <>
                  {isPendingInsuranceTypes ? (
                    <Spinner />
                  ) : (
                    <RadioGroup
                      autoFocus
                      optionsClassName="grid gap-2"
                      id="insurance-type"
                      onChange={(value) => {
                        field.onChange(value);
                      }}
                      options={formattedInsuranceTypes}
                      selectedValue={field.value}
                    />
                  )}
                </>
              )}
            />
            <FieldError error={errors.insuranceType} />
          </div>
        </InputGroup>
      </div>
    </StepComponent>
  );
};

export default InsuranceTypeSelectionStep;
